import { Component } from '@theme/component';
import { getFocusableElements } from '@theme/focus';
import {
  debounce,
  getAnnouncementBarVisibleBlockSizePx,
  isClickedOutside,
  onAnimationEnd,
} from '@theme/utilities';

const POINTER_OPEN_TYPES = new Set(['mouse', 'pen', 'touch']);

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {AbortController | null} */
  #hostListenersAbort = null;

  /**
   * @param {EventTarget | null} target
   */
  #targetOutsideDialog = (target) =>
    target instanceof Element && this.contains(target) && !this.refs.dialog.contains(target);

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    this.#hostListenersAbort = new AbortController();
    const { signal } = this.#hostListenersAbort;

    this.addEventListener('pointerdown', this.#handleHostPointerDown, { capture: true, signal });
    this.addEventListener('touchstart', this.#handleHostTouchStart, {
      capture: true,
      passive: true,
      signal,
    });
    this.addEventListener('keydown', this.#handleHostKeyDown, { capture: true, signal });
    this.addEventListener('click', this.#handleHostClick, { capture: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#hostListenersAbort?.abort();
    this.#hostListenersAbort = null;

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  /** @param {PointerEvent} event */
  #handleHostPointerDown = (event) => {
    if (!(event instanceof PointerEvent)) return;
    if (!POINTER_OPEN_TYPES.has(event.pointerType)) return;
    if (!this.#targetOutsideDialog(event.target)) return;

    this.dataset.dialogOpenedWithPointer = 'true';
  };

  /** @param {TouchEvent} event */
  #handleHostTouchStart = (event) => {
    if (!this.#targetOutsideDialog(event.target)) return;
    if (this.dataset.dialogOpenedWithPointer === 'false') return;

    this.dataset.dialogOpenedWithPointer = 'true';
  };

  /** @param {KeyboardEvent} event */
  #handleHostKeyDown = (event) => {
    const target = event.target;

    if (!(target instanceof Element) || !this.contains(target)) return;

    const { dialog } = this.refs;

    if (!dialog.contains(target) && (event.key === 'Enter' || event.key === ' ')) {
      this.dataset.dialogOpenedWithPointer = 'false';
    }

    if (!dialog.open || (event.key !== 'Escape' && event.key !== 'Tab')) return;

    this.dataset.dialogKeyboardInModal = 'true';
  };

  /** @param {MouseEvent} event */
  #handleHostClick = (event) => {
    if (!this.#targetOutsideDialog(event.target)) return;
    if (this.dataset.dialogOpenedWithPointer === 'false') return;

    this.dataset.dialogOpenedWithPointer = 'true';
  };

  #takeRestoreOutlineSuppress() {
    const opened = this.dataset.dialogOpenedWithPointer;
    const usedKeyboardInModal = this.dataset.dialogKeyboardInModal === 'true';

    delete this.dataset.dialogOpenedWithPointer;
    delete this.dataset.dialogKeyboardInModal;

    return opened === 'true' && !usedKeyboardInModal;
  }

  #focusFirstFocusableInDialog() {
    const { dialog } = this.refs;
    const focusable = getFocusableElements(dialog);
    const target = focusable[0];

    if (!target) return;

    /** @type {FocusOptions & { focusVisible?: boolean }} */
    const opts = { preventScroll: true, focusVisible: true };

    const focus = () => {
      try {
        target.focus(opts);
      } catch {
        target.focus({ preventScroll: true });
      }
    };

    focus();
    queueMicrotask(focus);
    requestAnimationFrame(focus);
  }

  /** @param {'open' | 'close'} when */
  #suppressFocusedOutline(when) {
    const dialog = this.refs.dialog;

    /** @type {FocusOptions & { focusVisible?: boolean }} */
    const opts = { preventScroll: true, focusVisible: false };

    /** @type {HTMLElement | undefined} */
    let tracked;

    const detachTracked = () => {
      if (!tracked) return;
      delete tracked.dataset.dialogRestoreSuppressRing;
      tracked.removeEventListener('blur', onTrackedBlur);
      tracked = undefined;
    };

    const onTrackedBlur = () => detachTracked();

    /** @param {HTMLElement} el */
    const tag = (el) => {
      if (!(el instanceof HTMLElement)) return;

      detachTracked();
      tracked = el;
      tracked.dataset.dialogRestoreSuppressRing = 'true';
      tracked.addEventListener('blur', onTrackedBlur);

      try {
        tracked.focus(opts);
      } catch {
        tracked.focus({ preventScroll: true });
      }
    };

    const tryActive = () => {
      const el = document.activeElement;
      if (!(el instanceof HTMLElement)) return;

      if (when === 'open' && !dialog.contains(el)) return;

      tag(el);
    };

    tryActive();
    queueMicrotask(tryActive);
    requestAnimationFrame(tryActive);
    requestAnimationFrame(() => requestAnimationFrame(tryActive));

    if (when === 'open') {
      const stopListening = () => dialog.removeEventListener('focusin', onOpenFocusIn, true);

      /** @param {FocusEvent} event */
      const onOpenFocusIn = (event) => {
        const node = event.target;

        stopListening();

        if (!(node instanceof HTMLElement) || !dialog.contains(node)) return;

        tag(node);
      };

      dialog.addEventListener('focusin', onOpenFocusIn, true);

      requestAnimationFrame(() => {
        requestAnimationFrame(stopListening);
      });

      return;
    }

    const stopDoc = () => document.removeEventListener('focusin', onDocFocusIn, true);

    /** @param {FocusEvent} event */
    const onDocFocusIn = (event) => {
      const node = event.target;

      stopDoc();

      if (!(node instanceof HTMLElement)) return;

      tag(node);
    };

    document.addEventListener('focusin', onDocFocusIn, true);

    requestAnimationFrame(() => {
      requestAnimationFrame(stopDoc);
    });
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   * @param {Event} [event]
   */
  showDialog(event) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    if (this.dataset.dialogOpenedWithPointer !== 'false') {
      if (event instanceof KeyboardEvent) {
        this.dataset.dialogOpenedWithPointer = 'false';
      } else if (
        this.dataset.dialogOpenedWithPointer !== 'true' &&
        (!event ||
          (event instanceof PointerEvent && POINTER_OPEN_TYPES.has(event.pointerType)) ||
          event instanceof MouseEvent)
      ) {
        // No event = programmatic open (e.g. ATC auto-open) — treat like pointer, no ring.
        this.dataset.dialogOpenedWithPointer = 'true';
      }
    }

    this.dataset.dialogKeyboardInModal = '';

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      this.#lockPageScroll(scrollY);

      if (this.id === 'search-modal') {
        this.style.setProperty(
          '--announcement-bar-visible-height',
          `${getAnnouncementBarVisibleBlockSizePx()}px`
        );
      }

      if (this.dataset.dialogOpenedWithPointer === 'false') {
        const closeBtn = dialog.querySelector('.close-button');

        if (closeBtn instanceof HTMLElement) {
          delete closeBtn.dataset.dialogRestoreSuppressRing;
        }
      } else if (this.dataset.dialogOpenedWithPointer === 'true') {
        const closeBtn = dialog.querySelector('.close-button');

        if (closeBtn instanceof HTMLElement) {
          closeBtn.dataset.dialogRestoreSuppressRing = 'true';
        }
      }

      dialog.showModal();

      if (this.dataset.dialogOpenedWithPointer === 'true') {
        this.#suppressFocusedOutline('open');
      } else if (this.dataset.dialogOpenedWithPointer === 'false') {
        this.#focusFirstFocusableInDialog();
      }

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    const closeAnimationTarget =
      this.id === 'search-modal'
        ? [
            dialog.querySelector('.predictive-search-form__main'),
            dialog.querySelector('.predictive-search-form__header-inner'),
          ].filter((el) => el instanceof Element)
        : dialog;

    if (this.id === 'search-modal') await new Promise(requestAnimationFrame);

    await onAnimationEnd(closeAnimationTarget ?? dialog, undefined, {
      subtree: false,
    });

    this.#unlockPageScroll();

    const suppressRestoreOutline = this.#takeRestoreOutlineSuppress();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    if (suppressRestoreOutline) {
      this.#suppressFocusedOutline('close');
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Same cleanup as `closeDialog` (listeners, body scroll lock, scroll restore, `dialog.close()`,
   * `dialog:close`) but does not wait for the `dialog-closing` exit animation.
   *
   * Used when opening another modal immediately (e.g. Stylux after Quick add) so the first dialog
   * leaves the top layer and releases focus without stacking two modals or delaying on the closing animation.
   * The animation reset matches `closeDialog` so CSS keyframes do not get stuck mid-sequence.
   */
  closeDialogImmediate = () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;

    this.#unlockPageScroll();

    const suppressRestoreOutline = this.#takeRestoreOutlineSuppress();

    dialog.close();
    dialog.classList.remove('dialog-closing');
    dialog.style.animation = '';

    if (suppressRestoreOutline) {
      this.#suppressFocusedOutline('close');
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Cart drawer: avoid body { position: fixed } (full-document style recalc).
   * Use a dedicated html attribute so we do not clear details[scroll-lock] menus.
   */
  #usesLightScrollLock() {
    return this.localName === 'cart-drawer-component';
  }

  /**
   * @param {number} scrollY
   */
  #lockPageScroll(scrollY) {
    if (this.#usesLightScrollLock()) {
      document.documentElement.setAttribute('cart-drawer-scroll-lock', '');
      return;
    }

    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
  }

  #unlockPageScroll() {
    if (this.#usesLightScrollLock()) {
      document.documentElement.removeAttribute('cart-drawer-scroll-lock');
      return;
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /**
   * Toggles the dialog.
   * @param {Event} [event]
   */
  toggleDialog = (event) => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog(event);
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
